/* iceberg-ui */
import { Box } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Alert } from '@mui/material';

/*  Types */
import { Tenant } from 'application/Shared/types/tenant';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { tenantHasSupportAccess } from './functions';

const ExpiredTrialBanner = ({ tenant }: { tenant: Tenant }) => {
    const { t } = useTranslation();
    const hubType = tenant?.settings?.hubType || 'None';

    const isExpiredTrial =
        hubType === 'None' &&
        tenant?.settings?.trialEndDate &&
        new Date(tenant?.settings?.trialEndDate as string).getTime() <
            new Date().getTime();

    if (!isExpiredTrial) {
        return null;
    }

    return (
        <Alert severity="warning" sx={{ marginBottom: '16px' }}>
            {t('GlobalAdmin.Tenant.Banners.TrialEndDate.expired')}
        </Alert>
    );
};

const SupportAccessBanner = ({ tenant }: { tenant: Tenant }) => {
    const { t } = useTranslation();
    // If the tenant has support access enabled, don't show the banner.
    if (tenantHasSupportAccess(tenant)) {
        return null;
    }

    return (
        <Alert severity="warning" sx={{ marginBottom: '16px' }}>
            {t('GlobalAdmin.Tenant.Banners.SupportAccessDisabled')}
        </Alert>
    );
};

/**
 * A warning banner that displays a message for a partner tenant if necessary.
 * @param tenant - The tenant to display the banner for.
 * @param type - The type of banner to display.
 * @returns
 */
const PartnerWarningBanner = ({
    tenant,
    type
}: {
    tenant?: Tenant;
    type: 'supportAccess' | 'expiredTrial';
}) => {
    // Only show the banner if there is a tenant and they're a partner.
    if (!tenant?.partner) {
        return null;
    }

    if (type === 'supportAccess') {
        return <SupportAccessBanner tenant={tenant} />;
    }
    if (type === 'expiredTrial') {
        return <ExpiredTrialBanner tenant={tenant} />;
    }

    return null;
};

/**
 * A component that displays all the warning banners for a tenant.
 * If tenant is a partner, then partnerTenant will be undefined.
 * If there is a tenant AND a partnerTenant, then tenant is a client.
 * @param tenant - The tenant to display the banners for.
 * @param partnerTenant - The partner tenant to display the banners for.
 * @returns A component that displays all the warning banners for a tenant.
 */
export const Banners = ({
    tenant,
    partnerTenant
}: {
    tenant?: Tenant;
    partnerTenant?: Tenant;
}) => (
    <Box maxWidth={1280} mx="auto">
        <PartnerWarningBanner
            tenant={partnerTenant || tenant}
            type="expiredTrial"
        />
        <PartnerWarningBanner
            tenant={partnerTenant || tenant}
            type="supportAccess"
        />
    </Box>
);
